/**
 * useSubSubtaskOperations — CRUD operations for sub-subtasks within subtasks.
 */
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { supabase } from "@/integrations/supabase/client";

export const useSubSubtaskOperations = (invalidateGoals: () => Promise<void>) => {
  const { handleError, handleSuccess } = useErrorHandler();

  const addSubSubtask = async ({ goalTitle, todoId, subtaskId, subSubtaskText }: { goalTitle: string, todoId: string, subtaskId: string, subSubtaskText: string }) => {
    try {
      const { data, error } = await supabase
        .from('sub_subtasks')
        .insert([{
          subtask_id: subtaskId,
          text: subSubtaskText || "New sub-subtask",
          completed: false
        }])
        .select()
        .single();

      if (error) throw error;
      await invalidateGoals();
    } catch (error: any) {
      handleError(error, { title: "Error adding sub-subtask" });
    }
  };

  const deleteSubSubtask = async ({ goalTitle, todoId, subtaskId, subSubtaskId }: { goalTitle: string, todoId: string, subtaskId: string, subSubtaskId: string }) => {
    try {
      const { error } = await supabase
        .from('sub_subtasks')
        .delete()
        .eq('id', subSubtaskId);

      if (error) throw error;
      await invalidateGoals();
    } catch (error: any) {
      handleError(error, { title: "Error deleting sub-subtask" });
    }
  };

  const toggleSubSubtask = async ({ goalTitle, todoId, subtaskId, subSubtaskId }: { goalTitle: string, todoId: string, subtaskId: string, subSubtaskId: string }) => {
    try {
      const { data: subSubtask } = await supabase
        .from('sub_subtasks')
        .select('completed')
        .eq('id', subSubtaskId)
        .single();

      const { error } = await supabase
        .from('sub_subtasks')
        .update({ completed: !subSubtask?.completed })
        .eq('id', subSubtaskId);

      if (error) throw error;
      await invalidateGoals();
    } catch (error: any) {
      handleError(error, { title: "Error toggling sub-subtask" });
    }
  };

  return {
    addSubSubtask,
    deleteSubSubtask,
    toggleSubSubtask
  };
};
